"use client";

import * as React from "react";
import {Check, ChevronsUpDown} from "lucide-react";

import {cn} from "../../utils/utils.ts";
import {buttonVariants} from "./button";
import {Popover, PopoverContent, PopoverTrigger} from "./popover";
import {Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList} from "./command";

export interface ComboboxOption {
    value: string;
    label: string;
}

interface ComboboxProps {
    options: ComboboxOption[];
    value?: string;
    onValueChange?: (value: string) => void;
    placeholder?: string;
    searchPlaceholder?: string;
    emptyText?: string;
    disabled?: boolean;
    className?: string;
}

export function Combobox({
                            options,
                            value,
                            onValueChange,
                            placeholder,
                            searchPlaceholder,
                            emptyText,
                            disabled,
                            className,
                        }: ComboboxProps) {
    const [open, setOpen] = React.useState(false);
    const selected = options.find(option => option.value === value);

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <button
                    type="button"
                    role="combobox"
                    aria-expanded={open}
                    disabled={disabled}
                    className={cn(buttonVariants({variant: "outline"}), "h-9 w-full justify-between font-normal", className)}
                >
                    <span className={cn("truncate", !selected && "text-muted-foreground")}>
                        {selected ? selected.label : (placeholder ?? "")}
                    </span>
                    <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50"/>
                </button>
            </PopoverTrigger>
            <PopoverContent className="w-(--radix-popover-trigger-width) p-0" align="start">
                <Command>
                    <CommandInput placeholder={searchPlaceholder}/>
                    <CommandList>
                        <CommandEmpty>{emptyText}</CommandEmpty>
                        <CommandGroup>
                            {options.map(option => (
                                <CommandItem
                                    key={option.value}
                                    value={option.label}
                                    onSelect={() => {
                                        onValueChange?.(option.value === value ? "" : option.value);
                                        setOpen(false);
                                    }}
                                >
                                    <Check
                                        className={cn("mr-1 h-4 w-4", value === option.value ? "opacity-100" : "opacity-0")}/>
                                    {option.label}
                                </CommandItem>
                            ))}
                        </CommandGroup>
                    </CommandList>
                </Command>
            </PopoverContent>
        </Popover>
    );
}
